import React, { useState, useEffect, useMemo } from 'react';
import { createTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import styles from './AddressInput.module.scss';
import api from '../../../../api/apiSettings';
import InputWithAutocomplete from '../InputWithAutocomplete/InputWithAutocomplete';
import { minSymbolQTY, validationMessages } from '../../../../utils/constants';
import ValidationErrorLabel from '../../ValidationErrorLabel/ValidationErrorLabel';

export function AddressInput({ userData, updateUserData, disabled, edit, errors }) {
  const theme = createTheme({
    palette: {
      text: {
        disabled: '#283237'
      }
    }
  });
  const autoCompleteStyles = {
    borderBottom: '1px solid #d1d3d4',
    padding: '5px',
    '& .MuiOutlinedInput-root': {
      borderRadius: '0',
      padding: '0'
    },
    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
      border: 'none'
    }
  };

  const { AddressLine1, AddressLine2, City, State, Country } = userData || [];

  const [country, setCountry] = useState(Country);
  const [state, setState] = useState(State);
  const [city, setCity] = useState(City);

  const [autocompleteCountry, setAutocompleteCountry] = useState([]);
  const [autocompleteState, setAutocompleteState] = useState([]);
  const [autocompleteCities, setAutocompleteCities] = useState([]);
  const { t } = useTranslation();
  const validationMessage = useMemo(() => validationMessages(t), [t]);

  const clearSuggestions = (entity) => {
    switch (entity) {
      case 'country':
        setAutocompleteCountry([]);
        break;
      case 'state':
        setAutocompleteState([]);
        break;
      case 'city':
        setAutocompleteCities([]);
        break;
      default:
        break;
    }
  };

  const handlersByEntity = {
    country: async (value, ignoreSymbolsQty = true) => {
      if (!value) {
        setState('');
        setCity('');
        setAutocompleteCities([]);
      }
      if (value !== country) {
        setState('');
        setCity('');
      }
      setCountry(value);
      updateUserData((x) => ({ ...x, Country: value }));
      if ((!value || value.length !== minSymbolQTY.COUNTRY) && ignoreSymbolsQty) return;
      const result = await api.get(`v1/get-countries?keyword=${value}`);
      setAutocompleteCountry(result?.data?.Data?.map((item) => ({ label: item?.Name })) || []);
    },
    state: async (value, ignoreSymbolsQty = true) => {
      if (country === '') return;
      if (!value) {
        setCity('');
      }
      if (value !== state) {
        setCity('');
      }
      setState(value);
      updateUserData((x) => ({ ...x, State: value }));
      if ((!value || value.length < minSymbolQTY.STATE) && ignoreSymbolsQty) return;
      const result = !disabled && (await api.get(`v1/get-states-by-country?countryName=${country}&keyword=${value}`));
      setAutocompleteState(result?.data?.Data?.map((item) => ({ label: item?.Name })) || []);
    },
    city: async (value, ignoreSymbolQty = true) => {
      setCity(value);
      updateUserData((x) => ({ ...x, City: value }));
      if ((!value || value.length !== minSymbolQTY.CITY) && ignoreSymbolQty) return;
      const result = await api.get(`v1/get-cities-by-state?countryName=${country}&stataName=${state}&keyword=${value}`);
      setAutocompleteCities(result?.data?.Data?.map((item) => ({ label: item?.Name })) || []);
    }
  };

  const inputHandler = ({ entity, value }, ignoreSymbolQty) => {
    handlersByEntity[entity](value, ignoreSymbolQty);
  };

  useEffect(() => {
    setState(State);
    setCountry(Country);
    setCity(City);
  }, [Country, State, City]);

  return (
    <div className={styles.InputComponent}>
      <div className={styles.label}>{t('common.forms.location')}</div>
      <input
        disabled={disabled}
        className={styles.InputStyles}
        onChange={(e) => updateUserData((x) => ({ ...x, AddressLine1: e.target.value }))}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...(edit ? { defaultValue: AddressLine1 } : { value: AddressLine1 })}
        id="addressLine1"
        type="text"
        placeholder={t('common.forms.address1')}
      />
      <input
        disabled={disabled}
        className={styles.InputStyles}
        onChange={(e) => updateUserData((x) => ({ ...x, AddressLine2: e.target.value }))}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...(edit ? { defaultValue: AddressLine2 } : { value: AddressLine2 })}
        type="text"
        id="addressLine2"
        placeholder={t('common.forms.address2')}
      />
      <InputWithAutocomplete
        entity="country"
        error={errors?.country}
        theme={theme}
        autoCompleteStyles={autoCompleteStyles}
        disabled={disabled}
        autocompleteValues={autocompleteCountry}
        fieldValue={country}
        changeHandler={inputHandler}
        placeholder={`${t('common.forms.country')}${t('common.forms.required')}`}
        clearSuggestions={clearSuggestions}
      />
      {country.length !== 0 && country.length < minSymbolQTY.COUNTRY && (
        <ValidationErrorLabel message={validationMessage.COUNTRY_MIN_CHAR} />
      )}
      <div className={styles.stateLocation}>
        <div className="position-relative">
          <InputWithAutocomplete
            entity="state"
            error={errors?.state}
            theme={theme}
            autoCompleteStyles={autoCompleteStyles}
            disabled={disabled}
            autocompleteValues={autocompleteState}
            fieldValue={state}
            changeHandler={inputHandler}
            placeholder={`${t('common.forms.state')}${t('common.forms.optional')}`}
            clearSuggestions={clearSuggestions}
          />
          {state.length !== 0 && state.length < minSymbolQTY.STATE && (
            <ValidationErrorLabel message={validationMessage.STATE_MIN_CHAR} />
          )}
        </div>
        <div className="position-relative">
          <InputWithAutocomplete
            entity="city"
            error={errors?.city}
            theme={theme}
            autoCompleteStyles={autoCompleteStyles}
            disabled={disabled}
            autocompleteValues={autocompleteCities}
            fieldValue={city}
            changeHandler={inputHandler}
            placeholder={`${t('common.forms.city')}${t('common.forms.required')}`}
            clearSuggestions={clearSuggestions}
          />
          {city.length !== 0 && city.length < minSymbolQTY.CITY && (
            <ValidationErrorLabel message={validationMessage.CITY_MIN_CHAR} />
          )}
        </div>
      </div>
    </div>
  );
}
